import React, { useEffect, useRef, useState } from "react";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import JigsawBoard from "./JigsawBoard";
import MazeGame from "./MazeGame";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function MediaImage({ filename, alt, className }) {
  if (!filename) return null;
  return (
    <img
      src={s3ThemeAssetUrl(filename)}
      alt={alt || ""}
      className={className}
      referrerPolicy="no-referrer"
    />
  );
}

/** Blurry image that only sharpens once the correct answer is given. */
function BlurryImage({ filename, initialBlurPx, solved }) {
  const blur = solved ? 0 : initialBlurPx || 12;
  return (
    <div className="tg-media-single">
      <img
        src={s3ThemeAssetUrl(filename)}
        alt=""
        className="tg-media-img tg-media-img--blurry"
        style={{ filter: `blur(${blur}px)`, transition: "filter 0.6s ease" }}
        referrerPolicy="no-referrer"
      />
    </div>
  );
}

function isTouchPrimaryDevice() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
}

function googleMapsOpenUrl(gpsCode) {
  const m = String(gpsCode || "").match(
    /([\d.]+)\s*°\s*([NS])[,\s]+([\d.]+)\s*°\s*([EW])/i
  );
  if (!m) return null;
  const lat = m[2].toUpperCase() === "S" ? -Number(m[1]) : Number(m[1]);
  const lng = m[4].toUpperCase() === "W" ? -Number(m[3]) : Number(m[3]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return `https://maps.google.com/?q=${lat},${lng}`;
}

/**
 * Google Maps embed: mobile browsers often fail to deliver taps into the
 * iframe (parent scroll / overlays). Unlock on first tap; keep desktop live.
 */
function MapEmbed({ src, gpsCode }) {
  const [active, setActive] = useState(() => !isTouchPrimaryDevice());
  const wrapRef = useRef(null);
  const openUrl = googleMapsOpenUrl(gpsCode);

  useEffect(() => {
    setActive(!isTouchPrimaryDevice());
  }, [src]);

  useEffect(() => {
    if (!active || !isTouchPrimaryDevice()) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setActive(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [active]);

  return (
    <div className="tg-map-column">
      <div
        ref={wrapRef}
        className={`tg-coord-box tg-map-wrap${active ? " tg-map-wrap--active" : ""}`}
      >
        {gpsCode ? <div className="tg-gps-code">{gpsCode}</div> : null}
        <div className="tg-map-scroll">
          {src ? (
            <iframe
              title="Location map"
              src={src}
              className="tg-map-iframe"
              loading="eager"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              allow="fullscreen; accelerometer; gyroscope; geolocation"
              style={{ pointerEvents: active ? "auto" : "none" }}
            />
          ) : null}
        </div>
        {!active ? (
          <button
            type="button"
            className="tg-map-activate"
            aria-label="Tap to use map"
            onClick={() => setActive(true)}
          >
            Tap map to interact
          </button>
        ) : null}
      </div>
      {openUrl ? (
        <a
          className="tg-map-open-link"
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Google Maps
        </a>
      ) : null}
    </div>
  );
}

function RoundMedia({ round, solved }) {
  switch (round.type) {
    case "morse":
      return (
        <div className="tg-media-stack">
          {round.audio ? (
            <audio className="tg-media-audio" controls src={s3ThemeAssetUrl(round.audio)} />
          ) : null}
          <div className="tg-media-row">
            <MediaImage filename={round.image_left} className="tg-media-img" alt="clue" />
            <MediaImage filename={round.image_right} className="tg-media-img" alt="reference" />
          </div>
        </div>
      );
    case "shapeCipher":
      return (
        <div className="tg-media-row">
          <MediaImage filename={round.image_left} className="tg-media-img" alt="clue" />
          <MediaImage filename={round.image_right} className="tg-media-img" alt="reference" />
        </div>
      );
    case "coordinates":
      return (
        <div className="tg-media-row tg-coordinates-row">
          {round.image_left ? (
            <div className="tg-coord-box">
              <MediaImage
                filename={round.image_left}
                className="tg-media-img tg-coord-img"
                alt="clue"
              />
            </div>
          ) : null}
          <MapEmbed src={round.map_embed} gpsCode={round.gps_code} />
        </div>
      );
    case "pigpen":
      return (
        <div className="tg-media-stack">
          <div className="tg-media-row">
            <MediaImage filename={round.image_left} className="tg-media-img" alt="clue" />
            <MediaImage filename={round.image_right} className="tg-media-img" alt="cipher" />
          </div>
          <MediaImage filename={round.image_bottom} className="tg-media-chart" alt="cipher key" />
        </div>
      );
    case "blurry":
      return <BlurryImage filename={round.image} initialBlurPx={round.initialBlurPx} solved={solved} />;
    case "unscramble":
      return (
        <div className="tg-media-stack">
          <div className="tg-media-single">
            <MediaImage filename={round.image} className="tg-media-img" alt="location" />
          </div>
          {round.scrambledWord ? (
            <div className="tg-scrambled">{round.scrambledWord}</div>
          ) : null}
        </div>
      );
    default:
      return null;
  }
}

export default function RoundView({ round, disabled, onSolved, onSkip, buttonBg, buttonText }) {
  const [value, setValue] = useState("");
  const [state, setState] = useState("idle"); // idle | wrong | correct
  const [jigsawSolved, setJigsawSolved] = useState(false);
  const inputRef = useRef(null);

  const submitStyle = {
    ...(buttonBg && { backgroundColor: buttonBg }),
    ...(buttonText && { color: buttonText }),
  };

  useEffect(() => {
    setValue("");
    setState("idle");
    setJigsawSolved(false);
  }, [round?.roundNumber]);

  const isMaze = round.type === "maze";
  const isJigsaw = round.type === "jigsaw";

  // Live feedback as the user types: green + read-only + auto-solve when the
  // answer matches, neutral while the text is still a valid prefix, red once it
  // diverges from the answer.
  const handleChange = (raw) => {
    setValue(raw);
    if (disabled || state === "correct") return;
    const v = normalize(raw);
    const ans = normalize(round.answer);
    if (!v) {
      setState("idle");
      return;
    }
    if (v === ans) {
      setState("correct");
      onSolved({ answer: raw.trim(), isCorrect: true });
      return;
    }
    setState(ans.startsWith(v) ? "idle" : "wrong");
  };

  // Explicit submit (button / Enter) for handing in a final wrong answer; a
  // correct answer auto-solves on keypress above.
  const submit = () => {
    if (disabled || state === "correct") return;
    if (!value.trim()) {
      setState("wrong");
      return;
    }
    const ok = normalize(value) === normalize(round.answer);
    setState(ok ? "correct" : "wrong");
    onSolved({ answer: value.trim(), isCorrect: ok });
  };

  const handleKey = (e) => {
    if (e.key === "Enter") submit();
  };

  // The jigsaw answer box only appears once the picture is fully assembled.
  const showAnswerBox = !isMaze && (!isJigsaw || jigsawSolved);

  return (
    <div className="tg-round">
      <div className="tg-banner" style={{ backgroundColor: round.bannerColor || "#5a7a1e" }}>
        {round.state}
      </div>
      <h2 className="tg-round-title">{round.title}</h2>

      <div className="tg-media">
        {isMaze ? (
          <MazeGame
            round={round}
            disabled={disabled}
            onSolved={onSolved}
            buttonBg={buttonBg}
            buttonText={buttonText}
          />
        ) : isJigsaw ? (
          <JigsawBoard
            src={s3ThemeAssetUrl(round.jigsawimages)}
            rows={round.jigsawRows}
            cols={round.jigsawCols}
            onComplete={() => setJigsawSolved(true)}
          />
        ) : (
          <RoundMedia round={round} solved={state === "correct"} />
        )}
      </div>

      {isJigsaw && !jigsawSolved ? (
        <p className="tg-jigsaw-hint">Drag every piece into place to unlock the answer.</p>
      ) : null}

      <div className="tg-answer">
        {showAnswerBox ? (
          <>
            <input
              ref={inputRef}
              className={`tg-input${state === "wrong" ? " tg-input--wrong" : ""}${
                state === "correct" ? " tg-input--correct" : ""
              }`}
              placeholder="Insert answer here"
              value={value}
              disabled={disabled}
              readOnly={state === "correct"}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKey}
            />
            <button
              type="button"
              className="tg-submit"
              onClick={submit}
              disabled={disabled || state === "correct" || !value.trim()}
              style={submitStyle}
            >
              Submit
            </button>
          </>
        ) : null}

        {isMaze ? (
          <button type="button" className="tg-submit tg-skip-btn" onClick={onSkip} disabled={disabled}>
            Skip
          </button>
        ) : null}
      </div>
    </div>
  );
}
